import { memo, useState, useEffect } from "react";
import { Alert, Box, Button, CircularProgress, Grid2, Typography } from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import CustomTreeItem from "./CustomTreeItem";
import { useViewSlice } from "@store/slices/view";
import { ContentNode, ContentNodeType, Query } from "@/app/graphql/codegen";
import { useDocumentTree } from "@documents/hooks/useDocumentTree";
import { useParams, useNavigate } from "react-router-dom";
import { useSelectContentNodeInChat } from "@chat/hooks/useSelectContentNodeInChat";
import { getUnifiedDocumentLabel } from "@/utils/documentLabels";
import { RouteToSettings } from "@routing/RouteMap";
import { useQuery } from "@apollo/client";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import { isDocumentVisible } from "@/utils/documentDisplay";
import { naturalSort } from "@utils/sorting";
import { useSelector } from "react-redux";
import { selectDocumentFilters } from "@store/slices/documentSelection";
import { DocumentSortFieldEnum, DocumentSortOrderEnum } from "@/app/graphql/codegen";


interface DocumentTreeViewProps {
    isGraphEnabled: boolean;
    isDocumentSelectionDisabled?: boolean;
    lexiconMode?: boolean;
    mainOnClick?: (node: ContentNode) => void;
    documents?: any[]; // Optional prop for external documents (for lexicon mode)
}

const DocumentTreeView = memo(({ isGraphEnabled, isDocumentSelectionDisabled = false, lexiconMode, mainOnClick, documents: externalDocuments }: DocumentTreeViewProps) => {
    const { id: chatId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    
    // Externe Dokumente verwenden (für Lexikon‑Modus), sonst den internen Hook
    const { documents: hookDocuments, context, loading, error } = useDocumentTree(chatId);
    const documents = externalDocuments || hookDocuments;
    
    // Nutzereinstellungen zur Sichtbarkeit von Dokumenten laden
    const { data: userProfileData } = useQuery<Pick<Query, "userProfile">>(FETCH_USER_PROFILE);
    const userSettings = userProfileData?.userProfile?.chatSettings || {};
    
    // Standard: Originale/Änderungen ausgeblendet, außer im Lexikon‑Modus
    const showAmendments = lexiconMode ? true : (userSettings.showAmendments === true); // default false, immer true im Lexikon
    const showOriginals = lexiconMode ? true : (userSettings.showOriginals === true);   // default false, immer true im Lexikon
    const showSummaries = lexiconMode ? false : (userSettings.showSummaries === true);   // default false, immer false im Lexikon

    const [expandedItems, setExpandedItems] = useState<string[]>(["root"]);
    // Gestaffeltes Rendering: 0=Daten laden, 1=Baum aufbauen, 2=fertig
    const [renderState, setRenderState] = useState(0);

    // Sortierpräferenzen aus dem Store, um ggf. Server‑Reihenfolge zu überschreiben
    const filters = useSelector(selectDocumentFilters);

    const sortDocs = (arr: any[]) => {
        // Nur bei Sortierung nach Titel überschreiben; Datum/Größe kommt vom Server
        if (filters.sortField === DocumentSortFieldEnum.Title) {
            const sorted = arr.slice().sort((a: any, b: any) => naturalSort(a.reference || a.name || "", b.reference || b.name || ""));
            if (filters.sortOrder === DocumentSortOrderEnum.Desc) sorted.reverse();
            return sorted;
        }
        return arr;
    };

    // Verzögertes Rendering nach Datenladung
    useEffect(() => {
        if (loading) {
            setRenderState(0); // Daten werden geladen
        } else if (documents.length > 0) {
            // Rendering des Baums verzögern
            requestAnimationFrame(() => {
                setRenderState(1); // Baum wird aufgebaut

                // Vollständige Baumstruktur nach kurzer Verzögerung rendern
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        setRenderState(2); // Vollständig geladen
                    });
                }, 100);
            });
        } else {
            // Leerer Dokumentenfall
            setRenderState(2);
        }
    }, [loading, documents.length]);

    const handleItemsExpanded = (_event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpandedItems(nodeIds);
    };

    const { selectContentNode } = useSelectContentNodeInChat(chatId);

    const { showExtendedView } = useViewSlice();

    // Hinweis anzeigen, wenn keine Dokumente vorhanden sind (z. B. keine aktiven Firmen‑Dokumente)
    if (!loading && !error && documents.length === 0) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={200} sx={{ maxWidth: '100%' }}>
                <Typography variant="body1" color="text.secondary">
                    {lexiconMode ? "Keine Dokumente im Lexikon verfügbar" : "Keine Dokumente ausgewählt. Gehe zu den Einstellungen, um Dokumente zu aktivieren."}
                </Typography>
                <Button variant="contained" size="small" onClick={() => navigate(RouteToSettings.LAW_SELECTION)} sx={{ mt: 1 }}>
                    Zu den Einstellungen
                </Button>
            </Box>
        );
    }

    // Ladeindikator während Datenladen / Baumaufbau
    if (renderState < 2) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" minHeight={200} sx={{ maxWidth: '100%' }}>
                <CircularProgress size={30} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {renderState === 0 ? "Dokumente werden geladen..." : "Baumstruktur wird aufgebaut..."}
                </Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Fehler beim Laden der Dokumentstruktur: {error.message}</Alert>;
    }

    const groupDocumentsByCategory = (documents: any[]) => {
        const categories = new Map<string, any[]>();

        // Filter documents based on user settings
        const filteredDocuments = documents.filter((doc) =>
            isDocumentVisible(doc, { showOriginals, showAmendments })
        );

        // Identifiziere Haupt- und Änderungsdokumente
        const mainDocuments: any[] = [];
        const amendmentDocuments: any[] = [];

        // Erstelle eine Lookup-Map für Dokumente basierend auf ID
        const documentsById = new Map<string, any>();
        filteredDocuments.forEach((doc) => {
            documentsById.set(doc.id, doc);
        });

        // Sortiere in Haupt- und Änderungsdokumente
        filteredDocuments.forEach((doc) => {
            // Erstelle eine Kopie des Dokuments, um es veränderbar zu machen
            const docCopy = { ...doc };

            if (docCopy.amendsDocumentId) {
                // Dies ist ein Änderungsdokument
                amendmentDocuments.push(docCopy);
            } else {
                // Dies ist ein Hauptdokument
                mainDocuments.push(docCopy);
            }
        });

        // Füge Hauptdokumente zu Kategorien hinzu und verknüpfe mit ihren Änderungen
        mainDocuments.forEach((doc) => {
            // Finde alle Änderungsdokumente für dieses Dokument
            doc.amendments = amendmentDocuments.filter((amendment) => amendment.amendsDocumentId === doc.id);

            const category = doc.category || "Uncategorized";
            if (!categories.has(category)) {
                categories.set(category, []);
            }
            categories.get(category)?.push(doc);
        });

        return categories;
    };

    // Rekursive Funktion zur Darstellung von Knoten
    const renderContentNode = (node: ContentNode, jurisdiction: string, isAmendment: boolean = false) => {
        // Handle GraphSummary nodes depending on user setting
        if (node.type === ContentNodeType.GraphSummary) {
            if (!showSummaries) return null;
        } else {
            // Types that should always be hidden to avoid duplicates
            const alwaysHiddenSummaryTypes = new Set<ContentNodeType>([
                ContentNodeType.DetailedSummary,
                ContentNodeType.RequirementsSummary,
                ContentNodeType.Summary,
                ContentNodeType.FullSummary,
                ContentNodeType.References,
                ContentNodeType.Header,
                ContentNodeType.Toc,
            ]);

            if (alwaysHiddenSummaryTypes.has(node.type)) {
                return null;
            }
        }

        // Create custom label and tooltip for summary nodes
        let nodeLabel: string;
        let tooltipLabel: string;
        if (node.type === ContentNodeType.GraphSummary) {
            // Use document reference + "Zusammenfassung" for summary nodes
            const docRef = node.associatedOfficialDocument?.reference || node.metadata?.reference || "Dokument";
            nodeLabel = `Zusammenfassung: ${docRef}`;
            tooltipLabel = `Zusammenfassung: ${docRef}`;
        } else {
            // Use standard labeling for other nodes
            nodeLabel = getUnifiedDocumentLabel(node, {
                showParentPrefix: false,
            });
            tooltipLabel = node.metadata?.name || nodeLabel;
        }

        // Compute effective jurisdiction - inherit from parent when child has no real jurisdiction
        const effectiveJurisdiction =
            node.metadata?.jurisdiction && node.metadata.jurisdiction !== "NONE"
                ? node.metadata.jurisdiction
                : jurisdiction; // parent value

        const nodeSelected = context?.contentNodes.find((contextNode) => {
            if (!contextNode) return false;
            if (contextNode.id === node.id) {
                //console.log("Found selected node: ", contextNode.associatedOfficialDocument?.reference)
                return true;
            }
        });

        const handleSetOfficialDocument = (node: ContentNode) => {
            if (isGraphEnabled) return; // Disable selection when graph mode is enabled
            if (!node.associatedOfficialDocument) return;
            if (!node.associatedOfficialDocument.reference) return;

            // Prüfe, ob bereits ausgewählt - kann abgewählt werden
            if (nodeSelected) {
                selectContentNode(node.id, false);
                return;
            }

            // Dokument auswählen (der Hook behandelt bereits die Fälle "bereits ausgewählt" und "Kontext voll")
            selectContentNode(node.id, true);
        };

        // Check if document is not active for the company (for lexicon mode)
        const documentNotActive = lexiconMode && node.associatedOfficialDocument && node.associatedOfficialDocument.isActiveForCompany === false;

        // Determine if this item is expandable (has children or summaries when enabled)
        const hasSubSections = node.childNodes && node.childNodes.filter((n) => n.type === ContentNodeType.SubSection).length > 0;
        const hasSummariesWhenEnabled =
            showSummaries &&
            node.childNodes &&
            node.childNodes.some((cn: ContentNode) => cn.type === ContentNodeType.GraphSummary);
        const hasChildren = !!(
            hasSubSections ||
            hasSummariesWhenEnabled ||
            (node.childNodes && node.childNodes.length > 0)
        );

        return (
            <CustomTreeItem
                key={node.id}
                nodeId={node.id}
                nodeLabel={nodeLabel}
                tooltipLabel={tooltipLabel}
                hoverTitle={node.associatedOfficialDocument?.metadata?.hoverTitle || ""}
                nodeJurisdiction={effectiveJurisdiction}
                isMainContent={node.type === ContentNodeType.MainContent}
                isAmendment={isAmendment} // Markiere als Änderungsdokument wenn zutreffend
                documentNotActive={!!documentNotActive}
                hasChildren={hasChildren}
                lexiconMode={lexiconMode}
                mainOnClick={
                    mainOnClick
                        ? () => mainOnClick(node)
                        : () => {
                              if (isGraphEnabled) return; // Disable selection when graph mode is enabled
                              if (node.childNodes) {
                                  // Verhindert, dass bei Klick auf einen TreeViewItem das Gesetz verwendet wird, wenn es noch Subdokumente enthält
                                  if (node.childNodes.filter((n) => n.type === ContentNodeType.SubSection).length > 0) {
                                      return;
                                  }

                                  // erwünschtes Default behaviour
                                  handleSetOfficialDocument(node);
                              }
                          }
                }
                mainAvailable={!lexiconMode && !isGraphEnabled && (node.isPromptable || false)} // Original logic: hide checkboxes in lexicon mode
                mainSelected={!!nodeSelected}
                isGraphEnabled={isGraphEnabled}
                onToggleMain={lexiconMode ? () => {} : () => {
                    if (isGraphEnabled) return; // Disable selection when graph mode is enabled

                    // Wenn abwählen
                    if (nodeSelected) {
                        selectContentNode(node.id, false);
                    } else {
                        // Wenn auswählen (Hook behandelt bereits die Fälle "bereits ausgewählt" und "Kontext voll")
                        selectContentNode(node.id, true);
                    }
                }}
            >
                {/* Sortiere alle Kinderknoten alphabetisch mit natürlicher Sortierung */}
                {node.childNodes
                    ?.slice()
                    .sort((a, b) => {
                        // Bezeichnungen für beide Knoten mit der einheitlichen Funktion ermitteln
                        const aLabel = getUnifiedDocumentLabel(a, { showParentPrefix: false });
                        const bLabel = getUnifiedDocumentLabel(b, { showParentPrefix: false });

                        // Natürliche Sortierung für die Bezeichnungen verwenden
                        return naturalSort(aLabel, bLabel);
                    })
                    .map((child: ContentNode) => renderContentNode(child, effectiveJurisdiction, isAmendment))}
            </CustomTreeItem>
        );
    };

    // Hilfsfunktion zum Rendern eines offiziellen Dokuments (ggf. mit Änderungen)
    const renderOfficialDocument = (doc: any) => {
        // If there are no amendments, render main doc only
        if (!doc.amendments || doc.amendments.length === 0) {
            return doc.rootContentNode ? renderContentNode(doc.rootContentNode, doc.jurisdiction) : null;
        }

        // Render main document with amendments as children
        return doc.rootContentNode ? (
            <CustomTreeItem
                key={doc.rootContentNode.id}
                nodeId={doc.rootContentNode.id}
                nodeLabel={getUnifiedDocumentLabel(doc.rootContentNode, { showParentPrefix: false })}
                nodeJurisdiction={doc.rootContentNode.metadata?.jurisdiction || doc.jurisdiction}
                isMainContent={true}
                hasChildren={!!(
                    doc.rootContentNode.childNodes?.length > 0 ||
                    doc.amendments?.length > 0 ||
                    (showSummaries &&
                        doc.rootContentNode.childNodes?.some(
                            (cn: ContentNode) => cn.type === ContentNodeType.GraphSummary,
                        ))
                )}
                lexiconMode={lexiconMode}
                mainOnClick={mainOnClick ? () => doc.rootContentNode && mainOnClick(doc.rootContentNode) : undefined}
                isGraphEnabled={isGraphEnabled}
                mainAvailable={!lexiconMode && !isGraphEnabled && (doc.rootContentNode.isPromptable || false)}
                mainSelected={!!context?.contentNodes.find((node) => node?.id === doc.rootContentNode.id)}
                hoverTitle={doc.metadata?.hoverTitle || ""}
                onToggleMain={lexiconMode ? () => {} : () => {
                    if (isGraphEnabled) return; // Disable selection when graph mode is enabled

                    if (context?.contentNodes.find((node) => node?.id === doc.rootContentNode.id)) {
                        selectContentNode(doc.rootContentNode.id, false);
                    } else {
                        selectContentNode(doc.rootContentNode.id, true);
                    }
                }}
                summarySelected={false}
            >
                {doc.rootContentNode.childNodes
                    ?.slice()
                    .sort((a: ContentNode, b: ContentNode) => {
                        const aLabel = getUnifiedDocumentLabel(a, { showParentPrefix: false });
                        const bLabel = getUnifiedDocumentLabel(b, { showParentPrefix: false });
                        return naturalSort(aLabel, bLabel);
                    })
                    .map((child: ContentNode) =>
                        renderContentNode(child, doc.jurisdiction, false),
                    )}

                {doc.amendments
                    .slice()
                    .sort((a: any, b: any) =>
                        naturalSort(a.reference || a.name || "", b.reference || b.name || ""),
                    )
                    .map((amendment: any) =>
                        amendment.rootContentNode
                            ? renderContentNode(amendment.rootContentNode, doc.jurisdiction, true)
                            : null,
                    )}
            </CustomTreeItem>
        ) : null;
    };

    return (
        // Nicht‑scrollender Wrapper. Scrollen erfolgt in der übergeordneten Box in DocumentSidebar
        <Grid2
            size={12}
            pb={2}
            sx={{
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
                "& .MuiTreeItem-group": { marginLeft: 2 },
                opacity: isGraphEnabled ? 0.66 : 1,
            }}
            border={`${showExtendedView ? 1 : 0}px solid green`}
        >
            <SimpleTreeView
                aria-label="document tree"
                defaultExpandedItems={["root"]}
                expandedItems={expandedItems}
                onExpandedItemsChange={handleItemsExpanded}
                sx={{
                    width: "100%",
                    "& .MuiTreeItem-content": {
                        padding: 0,
                    },
                }}
            >
                {(() => {
                    const categorizedDocs = groupDocumentsByCategory(documents);
                    // Sortiere die Kategorien mit natürlicher Sortierung

                    return Array.from(categorizedDocs.entries())
                        .sort((a, b) => naturalSort(a[0], b[0]))
                        .map(([category, docs]) => {
                            // First group documents within a category by group (undefined → "__UNGROUPED__")
                            const groupedDocs = new Map<string, any[]>();
                            docs.forEach((d) => {
                                // Use the new nullable `groupName` field (falls back to placeholder when absent)
                                const key = d.groupName || "__UNGROUPED__";
                                if (!groupedDocs.has(key)) groupedDocs.set(key, []);
                                groupedDocs.get(key)!.push(d);
                            });

                            return (
                                <CustomTreeItem
                                    key={category}
                                    nodeId={`category-${category}`}
                                    nodeLabel={category}
                                    isMainContent={false}
                                    isCategory={true}
                                    hasChildren={true}
                                    onToggleMain={() => {}}
                                    isGraphEnabled={isGraphEnabled}
                                    disableTooltip
                                >
                                    {(() => {
                                        const entries = Array.from(groupedDocs.entries());
                                        // Separate ungrouped
                                        const ungrouped = entries.find(([k]) => k === "__UNGROUPED__");
                                        const grouped = entries.filter(([k]) => k !== "__UNGROUPED__").sort((a, b) => naturalSort(a[0], b[0]));

                                        const rendered: any[] = [];

                                        // Render sorted named groups first
                                        grouped.forEach(([gName, gItems]) => {
                                            rendered.push(
                                                <CustomTreeItem
                                                    key={gName}
                                                    nodeId={`group-${category}-${gName}`}
                                                    nodeLabel={gName}
                                                    isCategory={true}
                                                    hasChildren={true}
                                                    onToggleMain={() => {}}
                                                    isGraphEnabled={isGraphEnabled}
                                                    disableTooltip
                                                >
                                                    {sortDocs(gItems).map((doc: any) => renderOfficialDocument(doc))}
                                                </CustomTreeItem>
                                            );
                                        });

                                        // Render ungrouped docs last
                                        if (ungrouped) {
                                            const [, gItems] = ungrouped;
                                            rendered.push(sortDocs(gItems).map((doc: any) => renderOfficialDocument(doc)));
                                        }

                                        return rendered;
                                    })()}
                                </CustomTreeItem>
                            );
                        });
                })()}
            </SimpleTreeView>
        </Grid2>
    );
});

export default DocumentTreeView;
